import { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from "react";
import axios from "axios";
import { useAuth, API } from "./AuthContext";

/**
 * Global whole-shop cart.
 *
 * Anonymous shoppers: cart lives in localStorage only.
 * Logged-in members / guests: cart syncs to the backend `/api/cart` endpoint so
 * it survives across devices and browsers. On login, we merge the local cart
 * into the server cart. On logout, the local cart is cleared but the server
 * cart persists for next login.
 *
 * Shape:
 *   items: [{ id, event_id, event_name, ticket_type_id, ticket_type_name,
 *             variant_id?, variant_name?, quantity, base_unit_price }]
 *   packages: [{ id, event_id, event_name, package_id, package_name,
 *                quantity, base_unit_price,
 *                variant_ids?: { [tier_id]: variant_id },
 *                variant_names?: { [tier_id]: variant_name } }]
 */
const STORAGE_KEY = "mymaitree_cart_v1";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const readInitial = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [], packages: [] };
    const parsed = JSON.parse(raw);
    return {
      items: Array.isArray(parsed.items) ? parsed.items : [],
      packages: Array.isArray(parsed.packages) ? parsed.packages : [],
    };
  } catch { return { items: [], packages: [] }; }
};

const _mkKey = (eventId, ttid, variantId) => `${eventId}::${ttid}::${variantId || "_"}`;
const _pkgKey = (eventId, pid) => `${eventId}::pkg::${pid}`;

// Merge two carts for the FIRST-time sync (anonymous local + server cart).
// For entries that appear in both, take the MAX quantity so we never
// accidentally double-count. Never SUM — that historically caused "refresh
// increased my cart" bugs.
const mergeCarts = (a, b) => {
  const itemMap = new Map();
  for (const it of [...a.items, ...b.items]) {
    const existing = itemMap.get(it.id);
    if (existing) itemMap.set(it.id, { ...existing, ...it, quantity: Math.max(existing.quantity, it.quantity) });
    else itemMap.set(it.id, { ...it });
  }
  const pkgMap = new Map();
  for (const p of [...a.packages, ...b.packages]) {
    const existing = pkgMap.get(p.id);
    if (existing) pkgMap.set(p.id, { ...existing, ...p, quantity: Math.max(existing.quantity, p.quantity) });
    else pkgMap.set(p.id, { ...p });
  }
  return { items: [...itemMap.values()], packages: [...pkgMap.values()] };
};

export const CartProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [cart, setCart] = useState(() => readInitial());
  const [isOpen, setIsOpen] = useState(false);
  // Track when we've completed the initial server-sync for the current login
  // session so we don't push a stale empty cart back up while fetching.
  const syncedForUserRef = useRef(null);

  // Persist to localStorage on every change (both anon and logged-in — localStorage
  // is the offline cache; server is the source of truth once logged in).
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cart)); } catch { /* quota / private mode — ignore */ }
  }, [cart]);

  // Cross-tab sync via storage events (anon or logged-in).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setCart(readInitial());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // On login: pull server cart. On the FIRST login for this user we also merge
  // any anonymous local cart the shopper built before logging in (so items they
  // added while browsing aren't lost). On subsequent logins / page refreshes we
  // treat the server as the source of truth — this avoids the "refresh doubles
  // cart quantities" bug where every reload re-summed local + server.
  const _syncFlagKey = (userKey) => `mymaitree_cart_synced_v1_${userKey}`;
  useEffect(() => {
    const userKey = user ? (user.customer_id || user.email || "user") : null;
    if (!user || !token) {
      // Anonymous state.
      if (syncedForUserRef.current !== null) {
        // We were previously logged in — clear the local cart on logout so the
        // next anonymous shopper doesn't inherit somebody else's tickets. Also
        // drop the sync flag so a future re-login triggers a fresh merge.
        try {
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(_syncFlagKey(syncedForUserRef.current));
        } catch { /* ignore */ }
        setCart({ items: [], packages: [] });
        syncedForUserRef.current = null;
      }
      return;
    }
    if (syncedForUserRef.current === userKey) return; // already synced this session
    // Guest OTP tokens are ephemeral; don't try to persist their cart.
    if (user.role && !["member", "guest"].includes(user.role)) {
      syncedForUserRef.current = userKey;
      return;
    }
    const local = readInitial();
    const isFirstSync = (() => {
      try { return localStorage.getItem(_syncFlagKey(userKey)) !== "1"; }
      catch { return true; }
    })();
    const client = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
    (async () => {
      try {
        const r = await client.get("/cart");
        const server = { items: r.data.items || [], packages: r.data.packages || [] };
        let next;
        if (isFirstSync) {
          // First-time sync for this user: merge anonymous local cart into server.
          const hasLocal = local.items.length + local.packages.length > 0;
          const hasServer = server.items.length + server.packages.length > 0;
          next = hasLocal && hasServer ? mergeCarts(server, local) : (hasServer ? server : local);
        } else {
          // Refresh: server is the source of truth. Never re-merge local
          // (would double quantities the debounced push already wrote up).
          next = server;
        }
        setCart(next);
        try { localStorage.setItem(_syncFlagKey(userKey), "1"); } catch { /* ignore */ }
        // Push merged state back up on first sync so both sides converge.
        if (isFirstSync && (local.items.length + local.packages.length > 0 || (server.items.length + server.packages.length === 0))) {
          try { await client.post("/cart", next); } catch { /* best-effort */ }
        }
      } catch (e) {
        // On failure, keep the local cart — user still sees their selection.
        console.warn("Cart sync failed; falling back to local cart", e);
      } finally {
        syncedForUserRef.current = userKey;
      }
    })();
  }, [user, token]);

  // Push cart changes to the server for logged-in shoppers (debounced).
  useEffect(() => {
    if (!user || !token) return;
    if (user.role && !["member", "guest"].includes(user.role)) return;
    const userKey = user.customer_id || user.email || "user";
    if (syncedForUserRef.current !== userKey) return; // wait for initial pull
    const handle = setTimeout(() => {
      const client = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
      client.post("/cart", cart).catch((e) => console.warn("Cart persist failed", e));
    }, 400);
    return () => clearTimeout(handle);
  }, [cart, user, token]);

  const setTicketQty = useCallback((entry, nextQty) => {
    // entry: { event_id, event_name, ticket_type_id, ticket_type_name, variant_id, variant_name, base_unit_price }
    const q = Math.max(0, Math.trunc(nextQty || 0));
    setCart(c => {
      const id = _mkKey(entry.event_id, entry.ticket_type_id, entry.variant_id);
      const existing = c.items.find(i => i.id === id);
      if (q === 0) {
        return { ...c, items: c.items.filter(i => i.id !== id) };
      }
      if (existing) {
        return { ...c, items: c.items.map(i => i.id === id ? { ...i, ...entry, id, quantity: q } : i) };
      }
      return { ...c, items: [...c.items, { ...entry, id, quantity: q }] };
    });
  }, []);

  const setPackageQty = useCallback((entry, nextQty) => {
    const q = Math.max(0, Math.trunc(nextQty || 0));
    // iter172: keep per-attendee variant arrays in lock-step with the
    // final quantity. Previously the cart-overlay -/+ buttons passed the
    // existing pkg back as `entry` with its stale full-length
    // `variant_selections` / `variant_names`, and the merge below
    // preserved that length even after decrementing (bug: 3 attendee
    // rows still shown after +2). Truncate/pad both arrays to `q` so
    // downstream consumers (cart UI + checkout) can't diverge.
    const _sync = (merged) => {
      const vsel = Array.isArray(merged.variant_selections) ? merged.variant_selections.slice(0, q) : [];
      const vname = Array.isArray(merged.variant_names) ? merged.variant_names.slice(0, q) : [];
      return { ...merged, variant_selections: vsel, variant_names: vname };
    };
    setCart(c => {
      const id = _pkgKey(entry.event_id, entry.package_id);
      const existing = c.packages.find(p => p.id === id);
      if (q === 0) {
        return { ...c, packages: c.packages.filter(p => p.id !== id) };
      }
      if (existing) {
        return { ...c, packages: c.packages.map(p => p.id === id ? _sync({ ...p, ...entry, id, quantity: q }) : p) };
      }
      return { ...c, packages: [...c.packages, _sync({ ...entry, id, quantity: q })] };
    });
  }, []);

  const getTicketQty = useCallback((event_id, ticket_type_id, variant_id) => {
    const id = _mkKey(event_id, ticket_type_id, variant_id);
    return cart.items.find(i => i.id === id)?.quantity || 0;
  }, [cart]);

  const getPackageQty = useCallback((event_id, package_id) => {
    const id = _pkgKey(event_id, package_id);
    return cart.packages.find(p => p.id === id)?.quantity || 0;
  }, [cart]);

  // iter189: full entry lookup so the product page can rehydrate its
  // per-attendee variant pickers from the cart's persisted
  // `variant_selections` / `variant_names` when the shopper returns from
  // checkout. Without this, PackageCard falls back to `_defaultCopy()` and
  // silently loses the shopper's picks (product tile shows "Veg" while the
  // cart still shows "Non Veg").
  const getPackageEntry = useCallback((event_id, package_id) => {
    const id = _pkgKey(event_id, package_id);
    return cart.packages.find(p => p.id === id) || null;
  }, [cart]);

  const removeItem = useCallback((id) => {
    setCart(c => ({ ...c, items: c.items.filter(i => i.id !== id) }));
  }, []);
  const removePackage = useCallback((id) => {
    setCart(c => ({ ...c, packages: c.packages.filter(p => p.id !== id) }));
  }, []);

  const clear = useCallback(() => {
    setCart({ items: [], packages: [] });
    // Fire-and-forget server clear for logged-in users so their next session
    // starts fresh after a successful checkout.
    if (user && token && (!user.role || ["member", "guest"].includes(user.role))) {
      axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } })
        .delete("/cart").catch(() => { /* best-effort */ });
    }
  }, [user, token]);

  // Derived: total quantity and base subtotal (final price shown at checkout).
  const count = useMemo(() =>
    cart.items.reduce((s, i) => s + i.quantity, 0) +
    cart.packages.reduce((s, p) => s + p.quantity, 0),
    [cart]
  );
  const subtotal = useMemo(() =>
    cart.items.reduce((s, i) => s + Number(i.base_unit_price || 0) * i.quantity, 0) +
    cart.packages.reduce((s, p) => s + Number(p.base_unit_price || 0) * p.quantity, 0),
    [cart]
  );

  // Grouped by event for the overlay UI.
  const groupedByEvent = useMemo(() => {
    const groups = {};
    for (const it of cart.items) {
      const eid = it.event_id || "_unknown";
      if (!groups[eid]) groups[eid] = { event_id: eid, event_name: it.event_name || "Event", items: [], packages: [] };
      groups[eid].items.push(it);
    }
    for (const p of cart.packages) {
      const eid = p.event_id || "_unknown";
      if (!groups[eid]) groups[eid] = { event_id: eid, event_name: p.event_name || "Event", items: [], packages: [] };
      groups[eid].packages.push(p);
    }
    return Object.values(groups);
  }, [cart]);

  // iter205: re-check saved-cart lines against live availability (sold out / off sale).
  const [unavailable, setUnavailable] = useState({});
  useEffect(() => {
    if (cart.items.length + cart.packages.length === 0) { setUnavailable({}); return undefined; }
    const handle = setTimeout(() => {
      axios.post(`${API}/ticketing/cart/availability`, {
        items: cart.items.map(i => ({ id: i.id, ticket_type_id: i.ticket_type_id, quantity: i.quantity })),
        packages: cart.packages.map(p => ({ id: p.id, package_id: p.package_id, quantity: p.quantity })),
      }).then(r => setUnavailable(r.data.unavailable || {})).catch(() => {});
    }, 300);
    return () => clearTimeout(handle);
  }, [cart, isOpen]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(v => !v), []);

  const value = {
    items: cart.items,
    packages: cart.packages,
    count,
    subtotal,
    groupedByEvent,
    setTicketQty,
    setPackageQty,
    getTicketQty,
    getPackageQty,
    getPackageEntry,
    removeItem,
    removePackage,
    clear,
    unavailable,
    isOpen,
    open,
    close,
    toggle,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
